import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type UserSearchResult } from "../../api/client";
import UserPicker from "../../components/UserPicker";
import { dateTimeShort } from "../../lib/format";

export default function ManagerOverridesAdmin() {
  const qc = useQueryClient();
  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ["admin-manager-overrides"],
    queryFn: api.admin.listManagerOverrides,
  });

  const [adding, setAdding] = useState(false);
  const [claimant, setClaimant] = useState<UserSearchResult | null>(null);
  const [manager, setManager] = useState<UserSearchResult | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.admin.upsertManagerOverride({
        claimant_entra_oid: claimant!.oid,
        claimant_email: claimant!.email,
        claimant_display_name: claimant!.displayName,
        override_manager_oid: manager!.oid,
        override_manager_email: manager!.email,
        override_manager_display_name: manager!.displayName,
        reason: reason.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-manager-overrides"] });
      setAdding(false);
      setClaimant(null);
      setManager(null);
      setReason("");
      setError(null);
    },
    onError: (e) => setError((e as Error).message),
  });

  const deactivate = useMutation({
    mutationFn: (id: number) => api.admin.deactivateManagerOverride(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-manager-overrides"] }),
  });

  const submit = () => {
    setError(null);
    if (!claimant) return setError("Pick the claimant");
    if (!manager) return setError("Pick the override approver");
    if (claimant.oid === manager.oid) return setError("Claimant and approver must be different people");
    if (!reason.trim()) return setError("Reason is required");
    save.mutate();
  };

  return (
    <div className="space-y-6">
      <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Back to admin</Link>

      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-liaise-navy">Manager overrides</h1>
          <p className="text-slate-600 mt-1">
            Pin a specific approver for a claimant when their Entra <code className="text-xs bg-slate-100 px-1 rounded">manager</code> attribute is wrong, missing, or temporarily covered.
            Override beats Entra and can be removed any time.
          </p>
        </div>
        {!adding && (
          <button
            onClick={() => { setAdding(true); setError(null); }}
            className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700"
          >
            + New override
          </button>
        )}
      </div>

      {adding && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
          <h2 className="font-medium text-liaise-navy">New override</h2>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Claimant</label>
            <UserPicker
              selected={claimant}
              onChange={setClaimant}
              placeholder="Search the claimant by name or email…"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Override approver</label>
            <UserPicker
              selected={manager}
              onChange={setManager}
              placeholder="Search the person who should approve their claims…"
              excludeOids={claimant ? [claimant.oid] : []}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Reason <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Manager on long-term leave; David covering for now"
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5"
            />
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}

          <div className="flex gap-3 pt-2">
            <button
              onClick={submit}
              disabled={save.isPending}
              className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700 disabled:opacity-50"
            >
              {save.isPending ? "Saving…" : "Pin override"}
            </button>
            <button
              onClick={() => { setAdding(false); setClaimant(null); setManager(null); setReason(""); setError(null); }}
              className="text-slate-600 px-4 py-2 hover:underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {isLoading && <div className="px-6 py-12 text-center text-slate-500">Loading…</div>}
        {loadError && (
          <div className="px-6 py-8 text-red-700 bg-red-50 border-b border-red-200 text-sm">
            <div className="font-medium">Failed to load overrides</div>
            <div className="font-mono text-xs mt-1">{(loadError as Error).message}</div>
          </div>
        )}
        {data && data.length === 0 && !isLoading && (
          <div className="px-6 py-12 text-center text-slate-500">
            No overrides. Everyone's manager comes from Entra by default.
          </div>
        )}
        {data && data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-6 py-3 font-medium">Claimant</th>
                <th className="px-6 py-3 font-medium">→ Override approver</th>
                <th className="px-6 py-3 font-medium">Reason</th>
                <th className="px-6 py-3 font-medium">Set</th>
                <th className="px-6 py-3 font-medium">Active</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((o) => (
                <tr key={o.id} className={!o.active ? "bg-slate-50/50 text-slate-400" : ""}>
                  <td className="px-6 py-3">
                    <Link to={`/admin/claimant/${o.claimant_entra_oid}`} className="font-medium text-slate-700 hover:text-liaise-navy hover:underline">
                      {o.claimant_display_name}
                    </Link>
                    <div className="text-xs text-slate-500">{o.claimant_email}</div>
                  </td>
                  <td className="px-6 py-3">
                    <Link to={`/admin/claimant/${o.override_manager_oid}`} className="font-medium text-slate-700 hover:text-liaise-navy hover:underline">
                      {o.override_manager_display_name}
                    </Link>
                    <div className="text-xs text-slate-500">{o.override_manager_email}</div>
                  </td>
                  <td className="px-6 py-3 text-slate-600 italic">"{o.reason}"</td>
                  <td className="px-6 py-3 text-slate-500 text-xs">
                    <div>{dateTimeShort(o.set_at)}</div>
                    <div>by {o.set_by_email}</div>
                  </td>
                  <td className="px-6 py-3">{o.active ? "✓" : "—"}</td>
                  <td className="px-6 py-3 text-right whitespace-nowrap">
                    {o.active && (
                      <button onClick={() => deactivate.mutate(o.id)} className="text-red-600 text-xs hover:underline">Remove</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
